import { inject } from '@angular/core';
import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { AuthService } from '../services/auth.service';

export const JwtInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  // Skip interceptor for auth endpoints (login, register) and public endpoints
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/health',
    '/auth/refresh',
  ];
  const isPublicEndpoint = publicEndpoints.some(endpoint => req.url.includes(endpoint));
  
  if (isPublicEndpoint) {
    return next(req);
  }
  
  // Get token from AuthService signal FIRST (most up-to-date, updated synchronously)
  let token: string | null = null;
  let tenantId: string | undefined = undefined;
  let auth: AuthService | null = null;
  
  try {
    auth = inject(AuthService);
    // Directly get token from signal (if available and initialized)
    token = auth.token();
    tenantId = auth.tenantId(); // Use tenantId() method for consistency
  } catch (e) {
    // AuthService injection might fail in some contexts (e.g., initial app load before providers are ready)
    console.warn('[JWT Interceptor] AuthService injection or signal read failed, using localStorage fallback', e);
  }
  
  // Always fallback to localStorage if token is not yet available from AuthService
  // This handles page reloads and cases where AuthService signals might not be initialized yet
  if (!token) {
    try {
      const stored = localStorage.getItem('mis-auth');
      console.log('[JWT Interceptor] DEBUG: Raw localStorage item (mis-auth):', stored ? stored.substring(0, 50) + '...' : 'null');
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('[JWT Interceptor] DEBUG: Parsed localStorage content:', parsed);
        token = parsed.token || parsed.accessToken || parsed.access_token || null;
        
        // Check if token is expired
        if (token) {
          try {
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              const now = Math.floor(Date.now() / 1000);
              if (payload.exp < now) {
                console.error('[JWT Interceptor] ❌ Token is expired, clearing auth');
                localStorage.removeItem('mis-auth');
                token = null;
              }
            }
          } catch (e) {
            console.error('[JWT Interceptor] ❌ Failed to check token expiration:', e);
          }
        }
        
        if (!tenantId) { // Only update if not already set from AuthService
          tenantId = parsed.user?.tenantId || parsed.tenantId || parsed.user?.user?.tenantId || undefined;
        }
        console.log('[JWT Interceptor] Token from localStorage:', token ? token.substring(0, 30) + '...' : 'null');
        console.log('[JWT Interceptor] TenantId from localStorage:', tenantId);
      } else {
        console.log('[JWT Interceptor] DEBUG: localStorage.getItem(mis-auth) returned null or empty.');
        
        // Try sessionStorage as fallback
        try {
          const sessionStored = sessionStorage.getItem('mis-auth');
          console.log('[JWT Interceptor] DEBUG: Trying sessionStorage fallback:', sessionStored ? sessionStored.substring(0, 50) + '...' : 'null');
          if (sessionStored) {
            const parsed = JSON.parse(sessionStored);
            token = parsed.token || parsed.accessToken || parsed.access_token || null;
            tenantId = parsed.user?.tenantId || parsed.tenantId || undefined;
            console.log('[JWT Interceptor] ✅ Found token in sessionStorage:', token ? token.substring(0, 30) + '...' : 'null');
          }
        } catch (e) {
          console.error('[JWT Interceptor] ❌ SessionStorage fallback failed:', e);
        }
        
        // Production fallback: Check if we're on production and try cookie-based storage
        if (!token && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
          try {
            console.log('[JWT Interceptor] 🌐 Production environment detected, trying cookie fallback');
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
              const [name, value] = cookie.trim().split('=');
              if (name === 'mis-auth' && value) {
                try {
                  const decoded = decodeURIComponent(value);
                  const parsed = JSON.parse(decoded);
                  token = parsed.token || parsed.accessToken || parsed.access_token || null;
                  tenantId = parsed.user?.tenantId || parsed.tenantId || undefined;
                  console.log('[JWT Interceptor] ✅ Found token in cookie:', token ? token.substring(0, 30) + '...' : 'null');
                  break;
                } catch (e) {
                  console.error('[JWT Interceptor] ❌ Cookie parsing failed:', e);
                }
              }
            }
          } catch (e) {
            console.error('[JWT Interceptor] ❌ Cookie fallback failed:', e);
          }
        }
      }
    } catch (error) {
      console.error('[JWT Interceptor] Failed to parse auth from localStorage:', error);
    }
  }
  
  // Build headers object
  const headers: Record<string, string> = {};
  
  // Add Authorization header if token exists
  if (token) {
    // Remove any existing "Bearer " prefix to avoid duplication, then add it
    const cleanToken = token.replace(/^Bearer\s+/i, '').trim();
    if (cleanToken) {
      headers['Authorization'] = `Bearer ${cleanToken}`;
    }
  }
  
  // Add tenant-id header if available
  if (tenantId) {
    headers['x-tenant-id'] = tenantId;
  }
  
  // Clone request with headers if we have a token
  if (token && headers['Authorization']) {
    console.log('[JWT Interceptor] ✅ Adding Authorization header to:', req.url);
    console.log('[JWT Interceptor] Token preview:', token.substring(0, 30) + '...');
    console.log('[JWT Interceptor] Full Authorization header:', headers['Authorization'].substring(0, 50) + '...');
    console.log('[JWT Interceptor] Request URL:', req.url);
    console.log('[JWT Interceptor] Request method:', req.method);
    console.log('[JWT Interceptor] Current localStorage token:', localStorage.getItem('mis-auth') ? 'EXISTS' : 'NULL');
    req = req.clone({
      setHeaders: headers
    });
  } else if (!isPublicEndpoint) {
    // No token found for protected endpoint - log error with detailed debug info
    console.error('[JWT Interceptor] ❌ No token found for protected endpoint:', req.url);
    console.error('[JWT Interceptor] ❌ Token exists:', !!token);
    console.error('[JWT Interceptor] ❌ Token length:', token?.length || 0);
    console.error('[JWT Interceptor] ❌ AuthService available:', !!auth);
    console.error('[JWT Interceptor] ❌ AuthService token:', auth ? !!auth.token() : 'AuthService null');
    console.error('[JWT Interceptor] ❌ Is public endpoint:', isPublicEndpoint);
    console.error('[JWT Interceptor] ❌ localStorage content:', localStorage.getItem('mis-auth'));
    try {
      const stored = localStorage.getItem('mis-auth');
      console.error('[JWT Interceptor] Debug info:', {
        hasToken: !!token,
        tokenLength: token?.length || 0,
        tokenPreview: token ? `${token.substring(0, 20)}...` : null,
        authServiceToken: auth ? !!auth.token() : 'AuthService not available',
        authServiceTokenValue: auth?.token() ? `${auth.token()!.substring(0, 20)}...` : null,
        localStorageExists: !!stored,
        localStorageContent: stored ? JSON.parse(stored) : null,
      });
    } catch (e) {
      console.error('[JWT Interceptor] Could not get debug info:', e);
    }
  }
  
  return next(req);
};


